import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {StickerItemSource} from '@Libraries/pmw-stock-media-library.types';
import type {StickerItemSourceType} from '@Libraries/pmw-stock-media-library.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {StreamingItem} from '@PosterWhiteboard/items/streaming-item.class';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import {ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import {EdgeType} from '@PosterWhiteboard/classes/item-effects.types';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';
import {getFramesForSprite} from '@Libraries/animated-sprite.library';
import {Sprite} from '@PosterWhiteboard/items/fabric-sprite';
import type {DeepPartial} from '@/global';

export interface StickerItemObject extends BaseItemObject {
  hashedFilename: string;
  duration: number;
  frameRate: number;
  effects: ItemEffectsObject;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
  stickerSource: StickerItemSourceType;
}

/**
 * Default min duration for which stickers should be played
 */
const STICKER_MIN_PLAY_DURATION = 10;

export class StickerItem extends StreamingItem {
  declare _fabricObject: Sprite;
  public gitype: ITEM_TYPE.STICKER = ITEM_TYPE.STICKER;
  public frameRate = 0;
  public hashedFilename = '';
  public duration = 0;
  public effects: ItemEffects;
  public htmlImageElement!: HTMLImageElement;
  public highResAnimatedSprite!: AnimatedSprite;
  public screenAnimatedSprite!: AnimatedSprite;
  public stickerSource!: StickerItemSourceType;

  public constructor(page: Page) {
    super(page);
    this.effects = new ItemEffects(this);
  }

  public shouldPauseAtStreamEnd(): boolean {
    return false;
  }

  public getStreamDuration(): number {
    return this.getDuration();
  }

  public isStreamingItemPlaying(): boolean {
    return !!this.fabricObject && !this.fabricObject.isPaused();
  }

  override get fabricObject(): Sprite {
    return this._fabricObject;
  }

  public override getPlayDuration(): number {
    return Math.max(this.getDuration(), STICKER_MIN_PLAY_DURATION);
  }

  public async getFabricObjectForItem(opts?: InitItemOpts): Promise<Sprite> {
    const currentSprite = this.getCurrentSprite();

    const frameImages = await getFramesForSprite(currentSprite);
    if (!frameImages[0]) {
      throw new Error(`No frame images for sprite: ${JSON.stringify(currentSprite)}`);
    }
    [this.htmlImageElement] = frameImages;

    return new Sprite(this.htmlImageElement, {
      ...super.getCommonOptions(),
      frameImages,
      frameHeight: this.htmlImageElement.height,
      frameWidth: currentSprite.frameWidth,
      frameTime: 1000 / currentSprite.frameRate,
      ...this.getScalesAndShadowToMaintainScaledDimensions(this.htmlImageElement.width, this.htmlImageElement.height, opts),
    });
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const styles: CopyableItemStyles = {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      textBorder: this.border.toObject(),
      effects: this.effects.toObject(),
    };

    if (this.effects.edgeType === EdgeType.CURVED) {
      styles.rx = (this.effects.edgeThickness / 28) * 100;
      styles.ry = (this.effects.edgeThickness / 28) * 100;
    }

    return styles;
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<StickerItemObject> = {};
    if (copiedStyles.effects !== undefined) {
      stylesObj.effects = copiedStyles.effects;
    }

    if (copiedStyles.rx !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        edgeType: EdgeType.CURVED,
        edgeThickness: (copiedStyles.rx / 100) * 28,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        tint: this.effects.tint,
        tintColor: this.effects.tintColor,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.fabricObject.getCurrentTime();
  }

  public override seek(time: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const seekTime = time % this.getDuration();
      this.fabricObject
        .seek(seekTime)
        .then(() => {
          this.page.fabricCanvas.requestRenderAll();
          resolve();
        })
        .catch(reject);
    });
  }

  public override async stop(): Promise<void> {
    void super.stop();
    await this.fabricObject.stop();
  }

  public override async play(): Promise<void> {
    await this.fabricObject.play();
  }

  public override async pause(): Promise<void> {
    void super.pause();
    await this.fabricObject.pause();
  }

  public async stopItemStream(): Promise<void> {
    if (!this.fabricObject.isPaused()) {
      await this.stop();
    }
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  public override copyVals(obj: DeepPartial<StickerItemObject>): void {
    const {effects, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.effects.copyVals(effects);
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.effects.applyItemEffects();
    await this.effects.applyBorderEffects();
  }

  public override onRemove(): void {
    super.onRemove();
    this.fabricObject.onRemove();
  }

  public isAiAnimatedSticker(): boolean {
    return this.stickerSource === StickerItemSource.PMW_AI_ANIMATED_STICKER;
  }

  public isAnimatedSticker(): boolean {
    return this.stickerSource === StickerItemSource.PMW_ANIMATED_STICKER;
  }

  private getCurrentSprite(): AnimatedSprite {
    if (this.page.poster.isHighRes) {
      return this.highResAnimatedSprite;
    }

    return this.screenAnimatedSprite;
  }

  public override toObject(): StickerItemObject {
    return {
      ...super.toObject(),
      effects: this.effects.toObject(),
      hashedFilename: this.hashedFilename,
      duration: this.duration,
      frameRate: this.frameRate,
      highResAnimatedSprite: this.highResAnimatedSprite,
      screenAnimatedSprite: this.screenAnimatedSprite,
      stickerSource: this.stickerSource,
    };
  }

  protected override onItemDoubleTapped(): void {
    getPosterUi().openPosterEditorItemEffectsModal();
  }
}
